/* Company-name lookup. Single source of truth for ticker → company name +
   sector across the Day Trading suite.

   - Loads ~10-15k US-listed common stocks + ETFs from /api/stock/companies/bulk
     once on mount.
   - Caches in localStorage with a 7-day TTL so subsequent app loads are instant.
   - Backend caches the same response for 24h — combined the user pays the
     full-fetch cost at most once a week and the server hits Polygon at most
     once a day.

   Usage:
     const { lookup, name, ready } = useCompanyNames();
     <span>{name("NVDA")}</span>
     // → "NVIDIA Corp."
*/
import { useEffect, useState, useCallback, useRef } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const LS_DATA_KEY = "dt_company_names";
const LS_TIME_KEY = "dt_company_names_time";
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

// Normalise a Polygon name. Polygon returns long-form legal names with
// "Common Stock" / "Class A" suffixes that are noisy in tight UI rows.
function _cleanName(n) {
  if (!n) return "";
  return n
    .replace(/\s+Common Stock(?:\s+(Class\s+[A-Z]))?$/i, (_m, cls) => cls ? ` ${cls}` : "")
    .replace(/\s+Class\s+([A-Z])\s+Ordinary Shares?$/i, " Class $1")
    .replace(/\s+Ordinary Shares?$/i, "")
    .replace(/\s+American Depositary Shares?(?:.*)$/i, " ADR")
    .replace(/\s+ETF$/i, " ETF")
    .trim();
}

// Module-level singleton: every Day Trading component shares the same map
// so we never load it twice across tab switches in a single session.
let _memCache = null;       // { [ticker]: { name, sector } }
let _inflight = null;       // Promise — dedupe simultaneous loads
const _subscribers = new Set();
function _notify() { for (const fn of _subscribers) try { fn(_memCache); } catch { /* noop */ } }

async function _loadFromBackend() {
  const res = await fetch(`${API}/api/stock/companies/bulk`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  const map = {};
  for (const c of (data?.companies || [])) {
    map[c.ticker] = { name: _cleanName(c.name), sector: c.sector || "" };
  }
  return map;
}

async function ensureCompanyNames() {
  if (_memCache) return _memCache;
  if (_inflight) return _inflight;

  _inflight = (async () => {
    // Try localStorage first
    try {
      const cached = localStorage.getItem(LS_DATA_KEY);
      const ts = parseInt(localStorage.getItem(LS_TIME_KEY) || "0", 10);
      if (cached && ts && (Date.now() - ts) < SEVEN_DAYS_MS) {
        _memCache = JSON.parse(cached);
        _notify();
        return _memCache;
      }
    } catch { /* localStorage may be disabled */ }

    // Fetch fresh
    try {
      const fresh = await _loadFromBackend();
      _memCache = fresh;
      try {
        localStorage.setItem(LS_DATA_KEY, JSON.stringify(fresh));
        localStorage.setItem(LS_TIME_KEY, String(Date.now()));
      } catch { /* quota exceeded — keep mem cache anyway */ }
      _notify();
      return fresh;
    } catch (e) {
      // On network failure, fall back to whatever stale data is in localStorage
      // even if it's older than 7 days — better than nothing.
      try {
        const stale = localStorage.getItem(LS_DATA_KEY);
        if (stale) {
          _memCache = JSON.parse(stale);
          _notify();
          return _memCache;
        }
      } catch { /* noop */ }
      _memCache = {};
      _notify();
      return _memCache;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

export function useCompanyNames() {
  const [map, setMap] = useState(_memCache);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (!_memCache) {
      ensureCompanyNames().then(m => { if (mounted.current) setMap(m); });
    }
    const onUpdate = (m) => { if (mounted.current) setMap(m); };
    _subscribers.add(onUpdate);
    return () => { mounted.current = false; _subscribers.delete(onUpdate); };
  }, []);

  const lookup = useCallback((ticker) => {
    if (!ticker) return null;
    const t = String(ticker).toUpperCase();
    return (map && map[t]) || null;
  }, [map]);

  const name = useCallback((ticker, fallback) => {
    const r = lookup(ticker);
    return r?.name || fallback || String(ticker || "").toUpperCase();
  }, [lookup]);

  const sector = useCallback((ticker, fallback = "") => {
    return lookup(ticker)?.sector || fallback;
  }, [lookup]);

  return { lookup, name, sector, ready: !!map };
}

// Imperative variant for non-React contexts (e.g. cron-time formatting).
export async function getCompanyNameAsync(ticker) {
  const m = await ensureCompanyNames();
  const t = String(ticker || "").toUpperCase();
  return m[t]?.name || t;
}
